import { Link } from "react-router-dom";

export default function Manifesto() {
  return (
    <div className="page-transition pt-[72px]" data-testid="manifesto-page">
      {/* Hero */}
      <section className="section bg-[#0c0c0c] border-b border-[#b8966a]/10">
        <div className="container-custom text-center">
          <h1 className="font-display text-3xl md:text-5xl text-[#f8f5f0] tracking-wide mb-6">
            Fleuris Noir
          </h1>
          <p className="overline mb-4">Brand Manifesto</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom max-w-3xl">
          {/* Opening */}
          <div className="mb-20 text-center">
            <p className="font-heading text-xl md:text-2xl text-[#f8f5f0] leading-relaxed italic mb-6">
              We do not arrange flowers.<br />
              We compose moments that refuse to be ordinary.
            </p>
            <p className="font-body text-[#a19d98] leading-loose">
              Fleuris Noir was created for those who understand that beauty is not decoration—it is language. 
              It is memory. It is presence. A quiet kind of power that does not ask to be noticed, 
              yet never goes unseen.
            </p>
          </div>

          <div className="mb-20 text-center">
            <p className="font-heading text-xl text-[#f8f5f0] leading-relaxed italic mb-6">
              We believe flowers are not gifts.<br />
              They are declarations.
            </p>
            <div className="font-body text-[#a19d98] leading-loose space-y-1">
              <p>Of love that is intentional.</p>
              <p>Of luxury that is felt, not announced.</p>
              <p>Of emotion translated into form, texture, and breath.</p>
            </div>
          </div>

          {/* Divider */}
          <div className="divider mb-20" />

          {/* The Philosophy */}
          <div className="mb-20">
            <p className="overline text-center mb-8">The Philosophy</p>
            <div className="text-center space-y-6">
              <p className="font-body text-[#a19d98] leading-loose">
                We exist in the space between softness and structure.<br />
                Between restraint and excess.<br />
                Between what is said—and what is understood without words.
              </p>
              <p className="font-body text-[#a19d98] leading-loose">
                Every composition is designed as an editorial object. Not arranged, but authored. 
                Not repetitive, but released as a moment in time that will never exist again in the same way.
              </p>
              <p className="font-heading text-lg text-[#f8f5f0] italic">
                We do not chase trends.<br />
                We curate atmosphere.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="divider mb-20" />

          {/* The Craft */}
          <div className="mb-20">
            <p className="overline text-center mb-8">The Craft</p>
            <div className="text-center space-y-6">
              <p className="font-body text-[#a19d98] leading-loose">
                Each Fleuris Noir composition is built with intention—layered like a story told in silence.
              </p>
              <p className="font-body text-[#a19d98] leading-loose">We consider:</p>
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto my-8">
                {[
                  { label: "Shape", desc: "as silhouette" },
                  { label: "Color", desc: "as emotion" },
                  { label: "Texture", desc: "as tension" },
                  { label: "Space", desc: "as luxury" }
                ].map((item) => (
                  <div key={item.label} className="border border-[#b8966a]/10 p-5 text-center">
                    <p className="font-heading text-lg text-[#f8f5f0]">{item.label}</p>
                    <p className="font-body text-sm text-[#b8966a] italic">{item.desc}</p>
                  </div>
                ))}
              </div>
              <p className="font-body text-[#a19d98] leading-loose">
                Nothing is accidental. Nothing is rushed. Nothing is mass-produced in feeling.
              </p>
              <p className="font-heading text-lg text-[#f8f5f0] italic">
                What leaves our studio is not inventory.<br />
                It is a limited expression of time.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="divider mb-20" />

          {/* The Experience */}
          <div className="mb-20">
            <p className="overline text-center mb-8">The Experience</p>
            <div className="text-center space-y-6">
              <p className="font-heading text-xl text-[#f8f5f0] italic mb-4">
                To receive Fleuris Noir is to enter a slower world.
              </p>
              <p className="font-body text-[#a19d98] leading-loose">A world where:</p>
              <div className="space-y-2 font-body text-[#a19d98]">
                <p>Deliveries feel like <span className="text-[#f8f5f0]">arrivals</span></p>
                <p>Bouquets feel like <span className="text-[#f8f5f0]">editions</span></p>
                <p>And moments feel <span className="text-[#f8f5f0]">curated</span>, not convenient</p>
              </div>
              <p className="font-heading text-lg text-[#f8f5f0] italic mt-8">
                We do not create for volume.<br />
                We create for meaning.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="divider mb-20" />

          {/* The Membership */}
          <div className="mb-20">
            <p className="overline text-center mb-8">The Membership</p>
            <div className="text-center space-y-6">
              <p className="font-heading text-lg text-[#f8f5f0] italic">
                Fleuris Noir is not just a shop.<br />
                It is a rhythm.
              </p>
              <p className="font-body text-[#a19d98] leading-loose">
                A recurring relationship with beauty that evolves seasonally, emotionally, intentionally. 
                For those who choose to live with flowers—not as decoration—but as atmosphere.
              </p>
              <p className="font-heading text-lg text-[#f8f5f0] italic">
                This is not consumption.<br />
                This is participation.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="divider mb-20" />

          {/* The Promise */}
          <div className="mb-20">
            <p className="overline text-center mb-8">The Promise</p>
            <div className="text-center space-y-4 font-body text-[#a19d98] leading-loose">
              <p>We will never dilute the work to scale faster.</p>
              <p>We will never reduce beauty to efficiency.</p>
              <p>We will never forget that luxury is not price—it is precision.</p>
              <div className="pt-4 space-y-1">
                <p className="text-[#f8f5f0]">Every stem matters.</p>
                <p className="text-[#f8f5f0]">Every composition carries weight.</p>
                <p className="text-[#f8f5f0]">Every delivery is a moment someone will remember.</p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="divider mb-20" />

          {/* Closing */}
          <div className="mb-12">
            <p className="overline text-center mb-8">Fleuris Noir Is for Those Who Understand</p>
            <div className="text-center space-y-4">
              <p className="font-body text-[#a19d98] leading-loose">
                That elegance is not loud.<br />
                That luxury is not excess.<br />
                That flowers are not products—but presence made visible.
              </p>
              <p className="font-heading text-xl text-[#f8f5f0] italic mt-8 leading-relaxed">
                And that what you surround yourself with...<br />
                becomes the language of your life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[#161616] border-t border-[#b8966a]/10">
        <div className="container-custom text-center">
          <Link to="/collection" className="btn-primary" data-testid="manifesto-shop-btn">
            Explore the Collection
          </Link>
        </div>
      </section>
    </div>
  );
}
